import { useState, useEffect, useMemo, type CSSProperties } from 'react';
import {
  Box,
  RefreshCw,
  Trash2,
  Download,
  ExternalLink,
  Search,
  Gauge,
  X,
} from 'lucide-react';
import { Toast } from '../../components/ui/FormField';
import { StatsCard } from '../../components/ui/StatsCard';
import {
  Field,
  TextInput,
  SelectInput,
  PrimaryButton,
  GhostButton,
} from '../../components/ui/Field';
import { createPortal } from 'react-dom';
import { siteApi, wellApi, cloudProcessApi } from '../../services/api-services';
import { useAuthStore } from '../../stores/auth-store';
import type { Site, CloudProcessItem, GeonomicFileItem } from '../../types/api';

// ─────────────────────────────────────────────────────────────
// ThreeDModelPage — Operations Deck redesign (Variation A)
//
// Layout:
//   • Stats strip (4 cards): Total · Complete · Processing · Queued
//   • Two side-by-side cards:
//       LEFT  · Create model     — Location → Well → LAS file pickers
//       RIGHT · Process & view  — From/To length, description, Process button
//   • Processed models grid with sortable headers
//   • Download Modal (portal) — Operations Deck shell
// ─────────────────────────────────────────────────────────────

type Status = 'Complete' | 'Processing' | 'Queued';

const COL_TEMPLATE =
  'minmax(200px, 1.7fr) minmax(180px, 1.4fr) 80px 80px 110px 110px 90px 90px 120px 120px';

const STATUS_COLOR: Record<Status, { fg: string; bg: string; border: string }> = {
  Complete: {
    fg: 'var(--color-teal)',
    bg: 'rgba(21,128,141,0.10)',
    border: 'rgba(21,128,141,0.28)',
  },
  Processing: {
    fg: 'var(--color-accent)',
    bg: 'rgba(11,74,111,0.08)',
    border: 'rgba(11,74,111,0.30)',
  },
  Queued: {
    fg: 'var(--color-warn)',
    bg: 'rgba(183,121,31,0.10)',
    border: 'rgba(183,121,31,0.30)',
  },
};

function statusOf(plyFileStatus: number | null | undefined): Status {
  if (plyFileStatus === 2) return 'Complete';
  if (plyFileStatus === 1) return 'Processing';
  return 'Queued';
}

export function ThreeDModelPage() {
  const authUser = useAuthStore((s) => s.user);
  const isSuperAdmin = authUser?.role === 'SuperAdmin';

  // Dropdowns
  const [sites, setSites] = useState<Site[]>([]);
  const [wells, setWells] = useState<{ id: number; wellName: string }[]>([]);
  const [lasFiles, setLasFiles] = useState<GeonomicFileItem[]>([]);

  // Selection
  const [siteId, setSiteId] = useState(0);
  const [wellId, setWellId] = useState(0);
  const [lasFileId, setLasFileId] = useState(0);

  // Process form
  const [fromLength, setFromLength] = useState('');
  const [toLength, setToLength] = useState('');
  const [description, setDescription] = useState('');
  const [processing, setProcessing] = useState(false);

  // Grid
  const [data, setData] = useState<CloudProcessItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [sortCol, setSortCol] = useState('Id');
  const [sortDir, setSortDir] = useState<'Ascending' | 'Descending'>('Descending');
  const [searchText, setSearchText] = useState('');
  const pageSize = 100;

  // Download modal
  const [downloadModal, setDownloadModal] = useState(false);
  const [selectedCloud, setSelectedCloud] = useState<CloudProcessItem | null>(null);
  const [downloading, setDownloading] = useState(false);

  // Toast
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  // ── Reference data ──
  useEffect(() => {
    const loadSites = isSuperAdmin
      ? siteApi.getAll({ limit: 1000 })
      : siteApi.getAllByCompanyId(Number(authUser?.companyId ?? 0), { limit: 1000 });
    loadSites.then((r) => setSites(r.entities ?? [])).catch(() => {});
  }, [isSuperAdmin, authUser?.companyId]);

  useEffect(() => {
    if (siteId > 0) {
      wellApi.getAllBySiteId(siteId, { limit: 1000 }).then((r) => setWells(r.entities ?? [])).catch(() => {});
    } else {
      setWells([]);
    }
    setWellId(0);
    setLasFiles([]);
    setLasFileId(0);
  }, [siteId]);

  useEffect(() => {
    if (wellId > 0) {
      cloudProcessApi.getGeonomicFiles(wellId).then((r) => setLasFiles(r.entities ?? [])).catch(() => setLasFiles([]));
    } else {
      setLasFiles([]);
    }
    setLasFileId(0);
  }, [wellId]);

  // ── Grid load ──
  const loadGrid = () => {
    if (siteId <= 0) {
      setData([]);
      setTotalCount(0);
      return;
    }
    setLoading(true);
    cloudProcessApi
      .getAllBySiteId(siteId, { offset, limit: pageSize, sortCol, sortDirection: sortDir })
      .then((r) => {
        setData((r.entities ?? []).filter((e) => !e.isDeleted));
        setTotalCount(r.numberofEntities ?? 0);
      })
      .catch(() => showToast('Failed to load models', 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadGrid(); }, [siteId, offset, sortCol, sortDir]);

  // ── Filters (client-side) ──
  const filteredData = useMemo(() => {
    if (!searchText.trim()) return data;
    const q = searchText.trim().toLowerCase();
    return data.filter((r) =>
      (r.wellName ?? '').toLowerCase().includes(q) ||
      (r.description ?? '').toLowerCase().includes(q),
    );
  }, [data, searchText]);

  // ── Stats ──
  const stats = useMemo(() => {
    const complete = data.filter((r) => r.plyFileStatus === 2).length;
    const inProgress = data.filter((r) => r.plyFileStatus === 1).length;
    const queued = data.length - complete - inProgress;
    return { total: totalCount, complete, inProgress, queued };
  }, [data, totalCount]);

  // ── Actions ──
  const handleProcess = async () => {
    if (!wellId || !lasFileId) {
      showToast('Please select Well and LAS file', 'error');
      return;
    }
    if (!fromLength || !toLength) {
      showToast('Please enter From and To lengths', 'error');
      return;
    }
    if (Number(toLength) < Number(fromLength)) {
      showToast('From Length should be less than To Length', 'error');
      return;
    }
    const selectedLas = lasFiles.find((f) => f.id === lasFileId);
    const drillSurvayId = selectedLas?.reference_DrillSurveyHead_Id ?? 0;
    setProcessing(true);
    showToast('Processing started…');
    try {
      await cloudProcessApi.process({
        wellGeonomicId: lasFileId,
        drillSurvayId,
        wellId,
        fromLength: Number(fromLength),
        toLength: Number(toLength),
        description,
        cloudHeadId: 0,
        perfectCloudStatus: 0,
        actualCloudStatus: 0,
        plyFileStatus: 0,
        id: 0,
      });
      showToast('Process completed. Check the status in the table.');
      loadGrid();
    } catch {
      showToast('Failed to process model', 'error');
    } finally {
      setProcessing(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Do you want to delete the 3D Model?')) return;
    try {
      await cloudProcessApi.delete(id);
      showToast('3D Model deleted successfully');
      loadGrid();
    } catch {
      showToast('Failed to delete', 'error');
    }
  };

  const openDownloadModal = async (row: CloudProcessItem) => {
    try {
      const r = await cloudProcessApi.getById(row.id);
      const cloud = r.entity ?? row;
      const well = wells.find((w) => w.id === (cloud.wellId ?? row.wellId));
      if (well) cloud.wellName = well.wellName;
      setSelectedCloud(cloud);
      setDownloadModal(true);
    } catch {
      setSelectedCloud(row);
      setDownloadModal(true);
    }
  };

  const handleDownload = async (cloudType: 'A' | 'P') => {
    if (!selectedCloud) return;
    const suffix = cloudType === 'A' ? 'Actual' : 'Perfect';
    const fileName = `${selectedCloud.wellName ?? 'Well'}_${selectedCloud.fromLength}m-${selectedCloud.toLength}m_${suffix}_${selectedCloud.id}.ply`;
    setDownloading(true);
    try {
      const blob = await cloudProcessApi.downloadPlyFile(selectedCloud.id, cloudType);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = fileName; a.click();
      URL.revokeObjectURL(url);
    } catch {
      showToast(`Failed to download ${fileName}`, 'error');
    } finally {
      setDownloading(false);
    }
  };

  const open3DViewer = (row: CloudProcessItem) => {
    const token = localStorage.getItem('geo_access_token') || '';
    const viewerBase = import.meta.env.VITE_3D_VIEWER_URL || 'https://gviewtest.geonomictech.com';
    window.open(`${viewerBase}/?fileId=${row.id}&wellId=${row.wellId}&token=${token}`, '_blank');
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const currentPage = Math.floor(offset / pageSize) + 1;

  const handleSort = (col: string) => {
    if (sortCol === col) {
      setSortDir(sortDir === 'Ascending' ? 'Descending' : 'Ascending');
    } else {
      setSortCol(col);
      setSortDir('Ascending');
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
      {/* Stats strip */}
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <StatsCard
          label="Total models"
          value={stats.total}
          sub={siteId > 0 ? 'At this location' : 'Pick a location to populate'}
          accent="var(--color-accent)"
          loading={loading}
        />
        <StatsCard
          label="Complete"
          value={stats.complete}
          sub="Ready to view / download"
          accent="var(--color-teal)"
          loading={loading}
        />
        <StatsCard
          label="Processing"
          value={stats.inProgress}
          sub="Reconstruction in progress"
          accent="#7c6ba8"
          loading={loading}
        />
        <StatsCard
          label="Queued"
          value={stats.queued}
          sub="Awaiting compute slot"
          accent="var(--color-warn)"
          loading={loading}
        />
      </div>

      {/* Two-card top row */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 16,
        }}
      >
        {/* Create Model */}
        <Panel icon={<Box size={17} />} title="Create model">
          <Field label="Location name">
            <SelectInput value={siteId} onChange={(e) => setSiteId(Number(e.target.value))}>
              <option value={0}>— Select location —</option>
              {sites.map((s) => (
                <option key={s.id} value={s.id}>{s.siteName}</option>
              ))}
            </SelectInput>
          </Field>
          <Field label="Well name">
            <SelectInput
              value={wellId}
              onChange={(e) => setWellId(Number(e.target.value))}
              disabled={!siteId}
            >
              <option value={0}>{siteId ? '— Select well —' : 'Select location first'}</option>
              {wells.map((w) => (
                <option key={w.id} value={w.id}>{w.wellName}</option>
              ))}
            </SelectInput>
          </Field>
          <Field label="LAS file name">
            <SelectInput
              value={lasFileId}
              onChange={(e) => setLasFileId(Number(e.target.value))}
              disabled={!wellId}
              style={{ fontFamily: 'var(--font-mono)' }}
            >
              <option value={0}>{wellId ? '— Select LAS file —' : 'Select well first'}</option>
              {lasFiles.map((f) => (
                <option key={f.id} value={f.id}>{f.fileName}</option>
              ))}
            </SelectInput>
          </Field>
        </Panel>

        {/* Process & View */}
        <Panel icon={<Gauge size={17} />} title="Process & view models">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Field label="From length (m)">
              <TextInput
                type="number"
                value={fromLength}
                placeholder="0"
                style={{ fontFamily: 'var(--font-mono)' }}
                onChange={(e) => setFromLength(e.target.value)}
              />
            </Field>
            <Field label="To length (m)">
              <TextInput
                type="number"
                value={toLength}
                placeholder="0"
                style={{ fontFamily: 'var(--font-mono)' }}
                onChange={(e) => setToLength(e.target.value)}
              />
            </Field>
          </div>
          <Field label="Model description">
            <TextInput
              value={description}
              placeholder="e.g. Production interval — post-clean run"
              onChange={(e) => setDescription(e.target.value)}
            />
          </Field>
          <div style={{ flex: 1 }} />
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
            <PrimaryButton
              onClick={handleProcess}
              disabled={processing || !wellId || !lasFileId}
            >
              <Box size={14} /> {processing ? 'Processing…' : 'Process model'}
            </PrimaryButton>
          </div>
        </Panel>
      </div>

      {/* Results table */}
      <div
        style={{
          background: 'var(--color-panel)',
          border: '1px solid var(--color-border)',
          borderRadius: 10,
          overflow: 'hidden',
        }}
      >
        {/* Toolbar */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '14px 18px',
            borderBottom: '1px solid var(--color-border)',
            flexWrap: 'wrap',
          }}
        >
          <div
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 16,
              fontWeight: 600,
              color: 'var(--color-text)',
              letterSpacing: -0.1,
            }}
          >
            Processed models
          </div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--color-text-dim)' }}>
            {loading ? 'Loading…' : `${filteredData.length} of ${totalCount}`}
          </div>

          <div style={{ flex: 1 }} />

          {/* Refresh */}
          <button
            onClick={loadGrid}
            disabled={loading}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '7px 12px',
              borderRadius: 7,
              border: '1px solid var(--color-border)',
              background: 'var(--color-panel)',
              color: 'var(--color-text-mid)',
              fontSize: 12.5,
              fontFamily: 'var(--font-body)',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1,
              transition: 'all 120ms',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-panel-alt)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--color-panel)')}
          >
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Refresh grid
          </button>

          {/* Search */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '7px 12px',
              background: 'var(--color-panel-alt)',
              border: '1px solid var(--color-border)',
              borderRadius: 8,
              width: 240,
              transition: 'border-color 180ms',
            }}
            className="focus-within:!border-[var(--color-accent-bright)]"
          >
            <Search size={14} style={{ color: 'var(--color-text-dim)' }} />
            <input
              placeholder="Search well, description…"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{
                border: 'none',
                outline: 'none',
                background: 'transparent',
                fontSize: 13,
                flex: 1,
                fontFamily: 'var(--font-body)',
                color: 'var(--color-text)',
                width: '100%',
              }}
            />
            {searchText && (
              <button
                onClick={() => setSearchText('')}
                aria-label="Clear search"
                title="Clear search"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: 18, height: 18, borderRadius: 9, padding: 0,
                  background: 'transparent', border: 'none',
                  color: 'var(--color-text-dim)', cursor: 'pointer', flexShrink: 0,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(15,23,32,0.08)';
                  e.currentTarget.style.color = 'var(--color-text)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = 'var(--color-text-dim)';
                }}
              >
                <X size={13} />
              </button>
            )}
          </div>
        </div>

        {/* Header row */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: COL_TEMPLATE,
            padding: '12px 18px',
            borderBottom: '1px solid var(--color-border)',
            background: 'var(--color-panel-alt)',
            fontSize: 12.5,
            letterSpacing: 0.3,
            color: 'var(--color-text-mid)',
            fontWeight: 600,
            fontFamily: 'var(--font-body)',
          }}
        >
          <ColHeader label="Well" col="WellName" activeCol={sortCol} dir={sortDir} onClick={handleSort} />
          <div>Description</div>
          <ColHeader label="From" col="FromLength" activeCol={sortCol} dir={sortDir} onClick={handleSort} align="right" />
          <ColHeader label="To" col="ToLength" activeCol={sortCol} dir={sortDir} onClick={handleSort} align="right" />
          <div style={{ textAlign: 'right' }}>Perfect vol</div>
          <div style={{ textAlign: 'right' }}>Actual vol</div>
          <div style={{ textAlign: 'right' }}>Diff</div>
          <div style={{ textAlign: 'right' }}>Time (s)</div>
          <div>Status</div>
          <div style={{ textAlign: 'right' }}>Actions</div>
        </div>

        {/* Rows */}
        {loading ? (
          <div style={{ padding: 60, textAlign: 'center', color: 'var(--color-text-dim)', fontSize: 13 }}>
            Loading…
          </div>
        ) : siteId === 0 ? (
          <div style={{ padding: 60, textAlign: 'center', color: 'var(--color-text-dim)' }}>
            <Box size={32} style={{ margin: '0 auto 12px', opacity: 0.4 }} />
            <div style={{ fontSize: 14, marginBottom: 4 }}>Pick a location to load 3D models</div>
            <div style={{ fontSize: 12 }}>The grid lists every processed model at the selected location.</div>
          </div>
        ) : filteredData.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center', color: 'var(--color-text-dim)' }}>
            <Box size={32} style={{ margin: '0 auto 12px', opacity: 0.4 }} />
            <div style={{ fontSize: 14, marginBottom: 4 }}>
              {searchText ? 'No models match your search' : 'No 3D models processed at this location yet'}
            </div>
            <div style={{ fontSize: 12 }}>
              {searchText ? 'Try a different keyword or clear the search.' : 'Use “Process model” above to start a reconstruction.'}
            </div>
          </div>
        ) : (
          <div>
            {filteredData.map((j, i) => (
              <ModelRow
                key={j.id ?? i}
                row={j}
                isLast={i === filteredData.length - 1}
                onView={() => open3DViewer(j)}
                onDownload={() => openDownloadModal(j)}
                onDelete={() => handleDelete(j.id)}
              />
            ))}
          </div>
        )}

        {/* Pagination footer */}
        <div
          style={{
            padding: '12px 18px',
            borderTop: '1px solid var(--color-border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: 12,
            color: 'var(--color-text-dim)',
            background: 'var(--color-panel-alt)',
          }}
        >
          <div>
            Showing{' '}
            <span style={{ color: 'var(--color-text-mid)', fontWeight: 500 }}>{filteredData.length}</span>{' '}
            of <span style={{ color: 'var(--color-text-mid)', fontWeight: 500 }}>{totalCount}</span>{' '}
            model{totalCount === 1 ? '' : 's'}
          </div>
          {totalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'var(--font-mono)' }}>
              <PaginationButton
                onClick={() => setOffset(Math.max(0, offset - pageSize))}
                disabled={offset === 0}
              >
                ←
              </PaginationButton>
              <span style={{ padding: '0 10px', color: 'var(--color-text-mid)' }}>
                {currentPage} / {totalPages}
              </span>
              <PaginationButton
                onClick={() => setOffset(offset + pageSize)}
                disabled={currentPage >= totalPages}
              >
                →
              </PaginationButton>
            </div>
          )}
        </div>
      </div>

      {/* Download modal */}
      <DownloadModal
        open={downloadModal}
        cloud={selectedCloud}
        downloading={downloading}
        onClose={() => setDownloadModal(false)}
        onDownload={handleDownload}
      />

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Panel — labelled card used for the Create / Process panels
// ─────────────────────────────────────────────────────────────

function Panel({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section
      style={{
        background: 'var(--color-panel)',
        border: '1px solid var(--color-border)',
        borderRadius: 10,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '14px 18px',
          borderBottom: '1px solid var(--color-border)',
          background: 'var(--color-panel-alt)',
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 7,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--color-panel)',
            border: '1px solid var(--color-border)',
            color: 'var(--color-accent)',
            flexShrink: 0,
          }}
        >
          {icon}
        </div>
        <h3
          style={{
            margin: 0,
            fontSize: 14.5,
            fontWeight: 600,
            fontFamily: 'var(--font-display)',
            color: 'var(--color-text)',
            letterSpacing: -0.1,
          }}
        >
          {title}
        </h3>
      </header>
      <div
        style={{
          padding: 18,
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
          flex: 1,
        }}
      >
        {children}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// ColHeader — local sortable header (cloud-process uses its own
// case keys: WellName / FromLength / ToLength — not the shared
// SortHeader's typical column names).
// ─────────────────────────────────────────────────────────────

function ColHeader({
  label,
  col,
  activeCol,
  dir,
  onClick,
  align = 'left',
}: {
  label: string;
  col: string;
  activeCol: string;
  dir: 'Ascending' | 'Descending';
  onClick: (col: string) => void;
  align?: 'left' | 'right';
}) {
  const active = activeCol === col;
  const [hover, setHover] = useState(false);
  const arrow = active ? (dir === 'Ascending' ? '▲' : '▼') : '↕';
  return (
    <button
      onClick={() => onClick(col)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        background: 'transparent',
        border: 'none',
        padding: 0,
        margin: 0,
        cursor: 'pointer',
        font: 'inherit',
        fontWeight: 600,
        letterSpacing: 0.3,
        color: active ? 'var(--color-accent)' : hover ? 'var(--color-text)' : 'var(--color-text-mid)',
        justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
        width: '100%',
        textAlign: align,
      }}
    >
      <span>{label}</span>
      <span style={{ fontSize: 9.5, opacity: active ? 1 : 0.5 }}>{arrow}</span>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────
// ModelRow
// ─────────────────────────────────────────────────────────────

function ModelRow({
  row: j,
  isLast,
  onView,
  onDownload,
  onDelete,
}: {
  row: CloudProcessItem;
  isLast: boolean;
  onView: () => void;
  onDownload: () => void;
  onDelete: () => void;
}) {
  const [hover, setHover] = useState(false);
  const status: Status = statusOf(j.plyFileStatus);
  const colors = STATUS_COLOR[status];

  const perfectVol = j.perfectVolume ?? null;
  const actualVol = j.actualVolume ?? null;
  const diff = j.diffVolume ?? null;

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'grid',
        gridTemplateColumns: COL_TEMPLATE,
        padding: '13px 18px',
        borderBottom: isLast ? 'none' : '1px solid var(--color-border)',
        alignItems: 'center',
        fontSize: 13.5,
        background: hover ? 'var(--color-panel-alt)' : 'transparent',
        transition: 'background 120ms',
      }}
    >
      {/* Well */}
      <Cell ellipsis title={j.wellName} style={{ fontWeight: 500 }}>
        {j.wellName || '—'}
      </Cell>

      {/* Description */}
      <Cell dim ellipsis title={j.description ?? ''}>
        {j.description || '—'}
      </Cell>

      {/* From / To */}
      <Cell mono dim align="right">{j.fromLength}</Cell>
      <Cell mono dim align="right">{j.toLength}</Cell>

      {/* Perfect / Actual volumes */}
      <Cell mono align="right">
        {perfectVol != null ? perfectVol.toFixed(1) : '—'}
      </Cell>
      <Cell mono align="right" dim={actualVol == null}>
        {actualVol != null ? actualVol.toFixed(1) : '—'}
      </Cell>

      {/* Diff (color-coded) */}
      <div
        style={{
          textAlign: 'right',
          fontFamily: 'var(--font-mono)',
          fontWeight: 500,
          fontSize: 12.5,
          color:
            diff == null
              ? 'var(--color-text-dim)'
              : diff < 0
                ? 'var(--color-bad)'
                : diff > 0
                  ? 'var(--color-teal)'
                  : 'var(--color-text-dim)',
        }}
      >
        {diff == null ? '—' : (diff > 0 ? '+' : '') + diff.toFixed(1)}
      </div>

      {/* Time taken */}
      <Cell mono dim align="right">
        {j.timeTakenToProcessModel ?? '—'}
      </Cell>

      {/* Status pill */}
      <div>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '3px 10px',
            borderRadius: 12,
            background: colors.bg,
            color: colors.fg,
            border: `1px solid ${colors.border}`,
            fontSize: 11.5,
            fontWeight: 500,
            letterSpacing: 0.2,
          }}
        >
          <span style={{ width: 6, height: 6, borderRadius: 3, background: colors.fg }} />
          {status}
        </span>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
        <IconButton title="Open 3D viewer" onClick={onView}>
          <ExternalLink size={15} />
        </IconButton>
        <IconButton title="Download PLY files" onClick={onDownload}>
          <Download size={15} />
        </IconButton>
        <IconButton title="Delete model" onClick={onDelete} hoverDanger>
          <Trash2 size={15} />
        </IconButton>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Download modal (Operations Deck shell)
// ─────────────────────────────────────────────────────────────

function DownloadModal({
  open,
  cloud,
  downloading,
  onClose,
  onDownload,
}: {
  open: boolean;
  cloud: CloudProcessItem | null;
  downloading: boolean;
  onClose: () => void;
  onDownload: (cloudType: 'A' | 'P') => void;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || !cloud) return null;

  const actualName = `${cloud.wellName}_${cloud.fromLength}m-${cloud.toLength}m_Actual_${cloud.id}.ply`;
  const perfectName = `${cloud.wellName}_${cloud.fromLength}m-${cloud.toLength}m_Perfect_${cloud.id}.ply`;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        background: 'rgba(15,23,32,0.45)',
        backdropFilter: 'blur(2px)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '10vh 16px 5vh',
        overflowY: 'auto',
      }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 560,
          background: 'var(--color-panel)',
          borderRadius: 14,
          boxShadow: '0 32px 80px rgba(0,0,0,0.30)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <header
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 16,
            padding: '20px 24px 16px',
            borderBottom: '1px solid var(--color-border)',
          }}
        >
          <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', minWidth: 0 }}>
            <div
              style={{
                width: 38, height: 38, borderRadius: 9,
                background: 'rgba(11,74,111,0.10)',
                color: 'var(--color-accent)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, marginTop: 2,
              }}
            >
              <Download size={20} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: 10.5,
                  textTransform: 'uppercase',
                  letterSpacing: 1.4,
                  color: 'var(--color-text-dim)',
                  fontFamily: 'var(--font-mono)',
                  fontWeight: 500,
                }}
              >
                3D model · Download
              </div>
              <h2
                style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 22,
                  fontWeight: 500,
                  letterSpacing: -0.3,
                  color: 'var(--color-text)',
                  marginTop: 3,
                  marginBottom: 4,
                  lineHeight: 1.2,
                }}
              >
                {cloud.wellName || 'Well'}
              </h2>
              <p style={{ fontSize: 12.5, color: 'var(--color-text-mid)', margin: 0, lineHeight: 1.45 }}>
                Interval {cloud.fromLength}–{cloud.toLength} m
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              width: 30, height: 30, borderRadius: 6,
              background: 'transparent', border: 'none',
              color: 'var(--color-text-dim)', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-panel-alt)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
          >
            <X size={18} />
          </button>
        </header>

        <div style={{ padding: '18px 24px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <DownloadOption
            name={actualName}
            label="Actual point cloud"
            sub="As-built reconstruction from sensor data"
            disabled={downloading}
            onClick={() => onDownload('A')}
          />
          <DownloadOption
            name={perfectName}
            label="Perfect point cloud"
            sub="Idealised geometry — useful as a reference baseline"
            disabled={downloading}
            onClick={() => onDownload('P')}
          />
        </div>

        <footer
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 10,
            padding: '14px 24px',
            background: 'var(--color-panel-alt)',
            borderTop: '1px solid var(--color-border)',
          }}
        >
          <GhostButton onClick={onClose}>Close</GhostButton>
        </footer>
      </div>
    </div>,
    document.body,
  );
}

function DownloadOption({
  name,
  label,
  sub,
  disabled,
  onClick,
}: {
  name: string;
  label: string;
  sub: string;
  disabled: boolean;
  onClick: () => void;
}) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '14px 14px',
        background: hover && !disabled ? 'var(--color-panel-alt)' : 'var(--color-panel)',
        border: `1px solid ${hover && !disabled ? 'var(--color-accent)' : 'var(--color-border)'}`,
        borderRadius: 8,
        cursor: disabled ? 'not-allowed' : 'pointer',
        textAlign: 'left',
        transition: 'all 120ms',
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <img src="/icons/ico-ply.png" alt="" style={{ width: 30, height: 30, flexShrink: 0 }} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text)' }}>{label}</div>
        <div style={{ fontSize: 12, color: 'var(--color-text-mid)', marginTop: 2 }}>{sub}</div>
        <div
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 11.5,
            color: 'var(--color-text-dim)',
            marginTop: 4,
            wordBreak: 'break-all',
          }}
        >
          {name}
        </div>
      </div>
      <Download size={16} style={{ color: 'var(--color-accent)', flexShrink: 0 }} />
    </button>
  );
}

// ─── Tiny cell + button helpers (same shape as the other pages)
function Cell({
  children,
  mono = false,
  dim = false,
  ellipsis = false,
  align = 'left',
  style,
  title,
}: {
  children: React.ReactNode;
  mono?: boolean;
  dim?: boolean;
  ellipsis?: boolean;
  align?: 'left' | 'right';
  style?: CSSProperties;
  title?: string;
}) {
  return (
    <div
      title={title}
      style={{
        fontFamily: mono ? 'var(--font-mono)' : 'var(--font-body)',
        color: dim ? 'var(--color-text-mid)' : 'var(--color-text)',
        fontSize: mono ? 12.5 : 13.5,
        whiteSpace: ellipsis ? 'nowrap' : undefined,
        overflow: ellipsis ? 'hidden' : undefined,
        textOverflow: ellipsis ? 'ellipsis' : undefined,
        textAlign: align,
        minWidth: 0,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function IconButton({
  children,
  onClick,
  title,
  hoverDanger,
}: {
  children: React.ReactNode;
  onClick: () => void;
  title: string;
  hoverDanger?: boolean;
}) {
  const [hover, setHover] = useState(false);
  const idleBg = 'var(--color-panel)';
  const idleBorder = 'var(--color-border)';
  const idleColor = hoverDanger ? '#a14a47' : 'var(--color-accent)';
  const hoverBg = hoverDanger ? 'rgba(185,28,28,0.08)' : 'rgba(11,74,111,0.08)';
  const hoverBorder = hoverDanger ? 'rgba(185,28,28,0.35)' : 'var(--color-accent)';
  const hoverColor = hoverDanger ? 'var(--color-bad)' : 'var(--color-accent-hover)';
  const active = hover;
  return (
    <button
      title={title}
      aria-label={title}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: 30,
        height: 30,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 7,
        border: `1px solid ${active ? hoverBorder : idleBorder}`,
        cursor: 'pointer',
        background: active ? hoverBg : idleBg,
        color: active ? hoverColor : idleColor,
        transition: 'all 120ms',
        transform: active ? 'translateY(-1px)' : 'none',
        boxShadow: active ? '0 2px 6px rgba(15,23,32,0.08)' : 'none',
      }}
    >
      {children}
    </button>
  );
}

function PaginationButton({
  children,
  onClick,
  disabled,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        width: 26,
        height: 26,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px solid var(--color-border)',
        background: 'var(--color-panel)',
        color: disabled ? 'var(--color-text-dim)' : 'var(--color-text-mid)',
        borderRadius: 4,
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontSize: 12,
        opacity: disabled ? 0.4 : 1,
      }}
    >
      {children}
    </button>
  );
}
